import * as moment from 'moment-timezone';

import { Router } from '@angular/router';

import {
  CountryCodeMap,
  dateStringFromISO8601,
  getActiveDateRangeFilterText,
  getFilterTextForDateRange,
  isLightColor,
  propExists,
  propExistsAndIsNonEmpty
} from './utils';

import {
  CustomCustomerField,
  OrganizationConfig
} from './models/organization-config';
import {
  CheckInEntryMethod,
  Customer,
  CustomerClimberAppTokenResponse,
  CustomerFilters,
  CustomerProfileDetails,
  Gender, getCheckInEntryMethodDisplay,
  getGenderDisplay,
  MembershipStatusSummary
} from './models/customer';
import { EventWaitlistSummary, ScheduledEvent } from './models/scheduled-event';
import { Association, AssociationAlert } from './models/association';
import { CustomerMembership, Membership } from './models/membership';
import { AccountService } from './services/account/account.service';
import { OrganizationConfigService } from './services/organization-config/organization-config.service';
import { EventType, ScheduledEventsColumn } from './models/event-type';
import { Proficiency } from './models/proficiency';
import { FilterMatchMode } from './models/filter-match-mode';
import { Challenge } from './models/challenge';
import { ScheduledEventsColumnType } from './models/event-type-enums';
import {
  AllthenticateInviteMode,
  CustomerProfileActionButton,
  CustomerProfileSidebarCard,
  DEFAULT_ACTION_BUTTON_ORDER,
  DEFAULT_SIDEBAR_ORDER,
  ProfileField
} from './models/organization-config-enums';
import { MembershipInterval } from './models/membership-enums';
import { CapitanLocation, LocationCheckInRequiredProficienciesMatchMode } from './models/location';
import { SavedReportViewReportType } from './models/saved-report-view';

export function getCustomerNameString(customer: Customer) {
  if (!customer) {
    return '';
  }
  return (customer.first_name + ' ' + customer.last_name).trim();
}

/**
 * The warning for a check-in without the location's required proficiencies
 * (PRO-1451), shared by the Check-In Site banner and the Staff Site profile.
 */
export function getMissingProficiencyWarningText(customerName: string,
                                                 locationName: string,
                                                 matchMode: LocationCheckInRequiredProficienciesMatchMode,
                                                 missingProficiencyNames: string[]): string {
  const names = missingProficiencyNames;

  if (matchMode === LocationCheckInRequiredProficienciesMatchMode.all) {
    if (names.length === 1) {
      return `${customerName} is missing the ${names[0]} proficiency required at ${locationName}.`;
    }
    return `${customerName} is missing proficiencies required at ${locationName}: ${names.join(', ')}.`;
  }

  if (names.length === 1) {
    return `${customerName} does not hold the ${names[0]} proficiency required at ${locationName}.`;
  }
  return `${customerName} must hold one of these proficiencies to climb at ${locationName}: ${names.join(', ')}.`;
}

export function getCustomerAgeAndBirthdayString(customer: Customer) {
  if (!customer) {
    return '';
  }

  const birthday = moment(customer.birthday, 'YYYY-MM-DD');
  const birthdayStr = birthday.format('ll');

  const age = moment().diff(birthday, 'years');
  const ageStr = age.toString();

  return ageStr + ' / ' + birthdayStr;
}

export function getCustomerStreetAddress(customer: Customer) {
  if (!customer) {
    return '';
  }

  if (customer.address_street_2 !== '') {
    return customer.address_street_1 + ', ' + customer.address_street_2;
  } else {
    return customer.address_street_1
  }
}

/**
 * Get the label for the given profile field.
 */
export function getCustomerFieldLabel(field: ProfileField,
                                      customCustomerFields: CustomCustomerField[]) {
  switch (field) {
    case ProfileField.name:
      return 'Name';
    case ProfileField.preferred_name:
      return 'Preferred Name';
    case ProfileField.email:
      return 'Email';
    case ProfileField.emailIsOptedIn:
      return 'Email Marketing Opt-In';
    case ProfileField.telephone:
      return 'Phone Number';
    case ProfileField.age_birthday:
      return 'Age / Birthday';
    case ProfileField.street_address:
      return 'Street Address';
    case ProfileField.city:
      return 'City';
    case ProfileField.state:
      return 'State/Province';
    case ProfileField.zipcode:
      return 'Postcode / Zip Code';
    case ProfileField.country:
      return 'Country';
    case ProfileField.gym_status:
      return 'Centre/Gym Status';
    case ProfileField.membership_status:
      return 'Membership Status';
    case ProfileField.punchpass_status:
      return 'Punchpass Status';
    case ProfileField.guest_passes:
      return 'Guest Passes';
    case ProfileField.guest_passes_all_customers:
      return 'Guest Passes (Any Customer)';
    case ProfileField.capitan_status:
      return 'Capitan Status';
    case ProfileField.id_number:
      return 'ID Number';
    case ProfileField.last_check_in:
      return 'Last Check-In';
    case ProfileField.check_in_count:
      return 'Check-In Count';
    case ProfileField.gender:
      return 'Gender';
    case ProfileField.pronouns:
      return 'Pronouns';
    case ProfileField.barcode:
      return 'Barcode';
    case ProfileField.homeLocation:
      return 'Home Location';
    case ProfileField.referralCode:
      return 'Referral Code';
    default:
      if (field.startsWith(ProfileField.customCustomerField)) {
        if (propExists(customCustomerFields)) {
          const ccfId = field.slice(3);
          for (const ccf of customCustomerFields) {
            if (ccf.id.toString() === ccfId) {
              return ccf.name;
            }
          }
          return 'Unknown Custom Field';
        } else {
          return 'Loading...';
        }
      }

      return 'Unknown Field';
  }
}

/**
 * Whether the org invites customers to Allthenticate for specific products
 * only (PRO-1430). Both checks matter: removing the integration does not reset
 * the invite mode. Mirrors `should_send_allthenticate_invite` on the server.
 */
export function usesSpecificAllthenticateProducts(
  config: OrganizationConfig
): boolean {
  return (
    !!config &&
    config.has_allthenticate_integration &&
    (config.allthenticate_invite_mode === AllthenticateInviteMode.specific)
  );
}

/**
 * Fill in the group-pricing fields a membership needs once "Is group" or
 * "Include younger tier" is turned on. The API ignores them when unused.
 */
export function ensureGroupTierDefaults(membership: Membership): void {
  if (!propExistsAndIsNonEmpty(membership.adult_discounts)) {
    membership.adult_discounts = [
      {
        number_in_tier: 1,
        discount_amount: 0
      },
      {
        number_in_tier: 2,
        discount_amount: 0
      }
    ];
  }
  if (!propExistsAndIsNonEmpty(membership.youth_discounts)) {
    membership.youth_discounts = [
      {
        number_in_tier: 1,
        discount_amount: 0
      },
      {
        number_in_tier: 2,
        discount_amount: 0
      }
    ];
  }
  if (!propExistsAndIsNonEmpty(membership.adult_tier_name)) {
    membership.adult_tier_name = 'Adult';
  }
  if (!propExistsAndIsNonEmpty(membership.youth_tier_name)) {
    membership.youth_tier_name = 'Youth';
  }
  if (!propExists(membership.allow_unlimited_adults)) {
    membership.allow_unlimited_adults = false;
  }
  if (!propExists(membership.allow_unlimited_youths)) {
    membership.allow_unlimited_youths = false;
  }
  if (!propExists(membership.freeze_fee)) {
    membership.freeze_fee = 0;
  }
  if (!propExists(membership.adult_freeze_fee)) {
    membership.adult_freeze_fee = 0;
  }
  if (!propExists(membership.youth_freeze_fee)) {
    membership.youth_freeze_fee = 0;
  }
}

/**
 * Get the display label for the given customer-profile sidebar card.
 */
export function getCustomerProfileSidebarCardLabel(card: CustomerProfileSidebarCard): string {
  switch (card) {
    case CustomerProfileSidebarCard.emergencyContacts:
      return 'Emergency Contacts';
    case CustomerProfileSidebarCard.medicalInfo:
      return 'Medical Info';
    case CustomerProfileSidebarCard.proficiency:
      return 'Proficiency';
    case CustomerProfileSidebarCard.notes:
      return 'Notes';
    case CustomerProfileSidebarCard.documents:
      return 'Documents';
    case CustomerProfileSidebarCard.challenges:
      return 'Challenges';
    case CustomerProfileSidebarCard.linkedAccounts:
      return 'Linked Accounts';
    case CustomerProfileSidebarCard.associations:
      return 'Associations';
    case CustomerProfileSidebarCard.tags:
      return 'Tags';
    case CustomerProfileSidebarCard.allthenticateStatus:
      return 'Allthenticate Status';
    default:
      return '';
  }
}

/**
 * A configured order of ids reconciled against a canonical list: known ids in
 * configured order (de-duplicated), then any canonical entries missing from it.
 */
export function reconcileOrder<T extends string>(configured: string[], canonical: T[]): T[] {
  const known = new Set<string>(canonical);
  const result: T[] = [];
  const seen = new Set<string>();

  if (configured) {
    for (const id of configured) {
      if (known.has(id) && !seen.has(id)) {
        result.push(id as T);
        seen.add(id);
      }
    }
  }

  for (const entry of canonical) {
    if (!seen.has(entry)) {
      result.push(entry);
    }
  }

  return result;
}

/**
 * Reconcile the configured customer-profile sidebar-card order against the
 * canonical set (see {@link reconcileOrder}).
 */
export function reconcileSidebarOrder(configured: string[]): CustomerProfileSidebarCard[] {
  return reconcileOrder(configured, DEFAULT_SIDEBAR_ORDER);
}

/**
 * Get the display label for the given customer-profile action button.
 */
export function getCustomerProfileActionButtonLabel(button: CustomerProfileActionButton): string {
  switch (button) {
    case CustomerProfileActionButton.openSellScreen:
      return 'Open Sell Screen';
    case CustomerProfileActionButton.addAlert:
      return 'Add Alert';
    case CustomerProfileActionButton.editProfile:
      return 'Edit Profile';
    case CustomerProfileActionButton.openClimberApp:
      return 'Open Climber App';
    case CustomerProfileActionButton.useRedemptionCode:
      return 'Use Redemption Code';
    default:
      return '';
  }
}

/**
 * Reconcile the configured customer-profile action-button order against the
 * canonical set (see {@link reconcileOrder}).
 */
export function reconcileActionButtonOrder(configured: string[]): CustomerProfileActionButton[] {
  return reconcileOrder(configured, DEFAULT_ACTION_BUTTON_ORDER);
}

/**
 * Get the given customer's value for the given profile field.
 */
export function getCustomerFieldValue(field: ProfileField,
                                      customer: Customer,
                                      customerProfileDetails: CustomerProfileDetails,
                                      loadingProfileDetails: boolean) {
  if (!customer) {
    return '';
  }

  switch (field) {
    case ProfileField.name:
      return getCustomerNameString(customer);
    case ProfileField.preferred_name:
      return customer.preferred_name ? customer.preferred_name : '--';
    case ProfileField.email:
      return customer.email ? customer.email : '--';
    case ProfileField.emailIsOptedIn:
      return customer.has_opted_in_to_marketing ? 'Yes' : 'No';
    case ProfileField.telephone:
      return customer.telephone ? customer.telephone : '--';
    case ProfileField.age_birthday:
      return getCustomerAgeAndBirthdayString(customer);
    case ProfileField.street_address:
      return getCustomerStreetAddress(customer) ? getCustomerStreetAddress(customer) : '--'
    case ProfileField.city:
      return customer.address_city ? customer.address_city : '--';
    case ProfileField.zipcode:
      return customer.address_postcode ? customer.address_postcode : '--';
    case ProfileField.state:
      return customer.address_state ? customer.address_state : '--';
    case ProfileField.country:
      return CountryCodeMap[customer.address_country] ? CountryCodeMap[customer.address_country] : '--';
    case ProfileField.gym_status:
      if (loadingProfileDetails) {
        return 'Loading...';
      } else if (customerProfileDetails.has_membership_recurring) {
        return 'Member, Recurring';
      } else if (customerProfileDetails.has_membership_prepaid) {
        return 'Member, Prepaid';
      } else if (customer.number_of_available_entry_passes > 0) {
        return 'Passholder';
      } else {
        return 'Guest';
      }
    case ProfileField.membership_status:
      // Derived server-side so every surface reports the same status (PRO-1503).
      if (loadingProfileDetails) {
        return 'Loading...';
      }
      {
        const statusDate = customerProfileDetails.membership_status_date;
        switch (customerProfileDetails.membership_status) {
          case MembershipStatusSummary.active:
            // A date only when the customer is scheduled to stop being a member;
            // a recurring membership's next bill date does not end it.
            return statusDate
              ? 'Member until ' + dateStringFromISO8601(statusDate)
              : 'Member';
          case MembershipStatusSummary.pendingStaffReview:
            return 'Pending Staff Review';
          case MembershipStatusSummary.frozen:
            return statusDate
              ? 'Frozen until ' + dateStringFromISO8601(statusDate)
              : 'Frozen';
          case MembershipStatusSummary.paymentFailed:
            return 'Payment Failed';
          case MembershipStatusSummary.paymentPending:
            return 'Payment Pending';
          case MembershipStatusSummary.awaitingPaymentMethod:
            return 'Awaiting Payment Method';
          case MembershipStatusSummary.futureStart:
            return statusDate
              ? 'Starts ' + dateStringFromISO8601(statusDate)
              : 'None';
          default:
            return 'None';
        }
      }
    case ProfileField.punchpass_status:
      if (loadingProfileDetails) {
        return 'Loading...';
      } else if (customer.number_of_available_entry_passes > 0) {
        if (customer.number_of_available_entry_passes > 1) {
          return customer.number_of_available_entry_passes.toString() + ' passes remaining';
        } else {
          return '1 pass remaining';
        }
      } else {
        return 'None';
      }
    case ProfileField.guest_passes:
      if (loadingProfileDetails) {
        return 'Loading...';
      } else {
        return customerProfileDetails.remaining_guest_passes.toString();
      }
    case ProfileField.guest_passes_all_customers:
      if (loadingProfileDetails) {
        return 'Loading...';
      } else {
        return customerProfileDetails.remaining_guest_passes_all_customers.toString();
      }
    case ProfileField.capitan_status:
      if (customer.has_capitan_login) {
        return 'Active';
      } else if (customer.capitan_invite_sent) {
        return 'Invited';
      } else {
        return 'Not invited';
      }
    case ProfileField.id_number:
      return customer.id.toString();
    case ProfileField.last_check_in:
      if (loadingProfileDetails) {
        return 'Loading...';
      } else if (!customerProfileDetails.last_check_in_date) {
        return 'None';
      } else {
        return dateStringFromISO8601(customerProfileDetails.last_check_in_date);
      }
    case ProfileField.check_in_count:
      return customer.check_in_count.toString();
    case ProfileField.gender:
      return getGenderDisplay(customer.gender);
    case ProfileField.pronouns:
      return customer.pronouns ? customer.pronouns : '--';
    case ProfileField.barcode:
      return customer.barcode ? customer.barcode : '--';
    case ProfileField.homeLocation:
      return customer.home_location_id ? customer.home_location_name : '--';
    case ProfileField.referralCode:
      if (loadingProfileDetails) {
        return 'Loading...';
      } else {
        return customerProfileDetails.referral_code;
      }
    default:
      if (field.startsWith(ProfileField.customCustomerField)) {
        const ccfId = field.slice(3);
        for (const ccfv of customer.custom_field_values) {
          if (ccfv.field_id.toString() === ccfId) {
            const valueStr = ccfv.value.toString();
            if (valueStr.length > 30) {
              return ccfv.value.slice(0, 27) + '...';
            } else {
              return ccfv.value;
            }
          }
        }
        return '--';
      }

      return 'Unknown Field';
  }
}

export function openClimberAppInNewTabAsCustomer(token: CustomerClimberAppTokenResponse,
                                                 customerId: number,
                                                 climberAppBaseUrl: string,
                                                 nextUrl: string) {
  const loginTokenUrl = climberAppBaseUrl + 'login-token/'
    + token.user_id.toString() + '/' + token.user_token + '/'
    + customerId.toString() + '/' + token.customer_token + '/';

  const url = loginTokenUrl + '?next=' + encodeURIComponent(nextUrl);

  window.open(url, '_blank').focus();
}

export function openClimberAppInNewTabAsStaff(token: CustomerClimberAppTokenResponse,
                                              climberAppBaseUrl: string,
                                              nextUrl: string) {
  const loginTokenUrl = climberAppBaseUrl + 'login-staff-token/' + token.user_id.toString() + '/' + token.user_token + '/';

  const url = loginTokenUrl + '?next=' + encodeURIComponent(nextUrl);

  window.open(url, '_blank').focus();
}

/**
 * Whether the event is full due to a reason other than it's primary capacity, such as facility capacity or booking limits.
 */
export function eventFullDueToSecondaryCapacity(scheduledEvent: ScheduledEvent): boolean {
  return (
    (scheduledEvent.reservation_count < scheduledEvent.capacity) &&
    (scheduledEvent.remaining_capacity <= 0)
  );
}

/**
 * Whether the event is full due to a reason other than it's primary capacity, such as facility capacity or booking limits.
 */
export function instructorsManuallyEdited(scheduledEvent: ScheduledEvent): boolean {
  return (
    (scheduledEvent.schedule_id !== null) &&
    scheduledEvent.instructors_set_manually
  );
}


/**
 * Get a dictionary indicating the style of the given customer alert.
 *
 * For use with ngStyle.
 */
export function getCustomerAlertStyle(alertColorHex: string) {
  return {
    color: isLightColor(alertColorHex) ? 'black' : 'white',
    'background-color': '#' + alertColorHex,
    'border-color': isLightColor(alertColorHex) ? 'gray' : '#' + alertColorHex
  };
}


/**
 * Get a dictionary indicating the style of the given association alert.
 *
 * For use with ngStyle.
 */
export function getAssociationAlertStyle(alert: AssociationAlert) {
  return {
    color: isLightColor(alert.color_hex) ? 'black' : 'white',
    'background-color': '#' + alert.color_hex,
    'border-color': isLightColor(alert.color_hex) ? 'gray' : '#' + alert.color_hex
  };
}


export function getScheduledEventRouterPath(event: ScheduledEvent): string[] {
  const dateString = event.start_datetime.split('T')[0];

  let routerPath: string[] = [];
  if (moment(event.end_datetime) < moment().utc()) {
    routerPath = ['/events/scheduled/past', dateString];
  } else if (moment(event.start_datetime) < moment().utc()) {
    routerPath = ['/events/scheduled/now'];
  } else {
    routerPath = ['/events/scheduled/upcoming', dateString];
  }

  routerPath.push('event');
  routerPath.push(event.event_part_id.toString());

  return routerPath;
}


export function getEventWaitlistSummaryRouterPath(summary: EventWaitlistSummary): string[] {
  // The first part's start and the last part's end stand in for the part's
  // own window. The summary's timestamps are UTC, while the day segment of
  // the path must be the event's local date, as the scheduled-events
  // endpoint's offset-localized strings give it.
  const localIso = (iso: string) => moment.tz(iso, summary.time_zone).format();
  return getScheduledEventRouterPath({
    start_datetime: localIso(summary.first_start_datetime),
    end_datetime: localIso(summary.last_end_datetime || summary.first_start_datetime),
    event_part_id: summary.first_event_part_id
  } as ScheduledEvent);
}


export function ownerIsOnlyMember(customerMembership: CustomerMembership) {
  return (customerMembership.customers_less_owner.length + customerMembership.unlinked_customers.length) === 0;
}


/**
 * Route the logged-in user to the homepage if they do not have the
 * can_access_reports permission.
 */
export function requireCanAccessReports(accountService: AccountService,
                                        router: Router) {
  if (accountService.myAccountInfoIsLoaded()) {
    if (!accountService.getMyAccount().can_access_reports) {
      router.navigate(['/']);
    }
  } else {
    accountService.loadMyAccountInfo().then(() => {
      if (!accountService.getMyAccount().can_access_reports) {
        router.navigate(['/']);
      }
    })
  }
}

/**
 * Active-filter tags for a multi-value filter with a match mode (PRO-1373):
 * one tag per value in "all" mode, one combined tag in "any" mode (per-value
 * tags would wrongly imply "all").
 */
function getMatchModeFilterTagsForNames(names: string[],
                                        matchMode: FilterMatchMode | undefined,
                                        anyModePrefix: string,
                                        getAllModeTag: (name: string) => string): string[] {
  if (names.length === 0) { return []; }

  if (matchMode === FilterMatchMode.any) {
    return [anyModePrefix + names.join(', ')];
  }

  return names.map(getAllModeTag);
}

/**
 * `getMatchModeFilterTagsForNames` over a comma-separated ID list; unparseable
 * IDs are skipped, unmatched ones render as "<unknown>".
 */
function getMatchModeFilterTags(idsCsv: string,
                                matchMode: FilterMatchMode | undefined,
                                getNameForId: (id: number) => string | null,
                                anyModePrefix: string,
                                getAllModeTag: (name: string) => string): string[] {
  const names: string[] = [];

  for (const filterPart of idsCsv.split(',')) {
    const parsedId = parseInt(filterPart, 10);
    if (isNaN(parsedId)) { continue; }

    const name = getNameForId(parsedId);

    names.push(name !== null ? name : '<unknown>');
  }

  return getMatchModeFilterTagsForNames(
    names, matchMode, anyModePrefix, getAllModeTag
  );
}

export function getActiveCustomerFilterTags(cfs: CustomerFilters,
                                            orgConfigService: OrganizationConfigService,
                                            eventTypes: EventType[] | null,
                                            proficiencies: Proficiency[] | null,
                                            associations: Association[] | null,
                                            challenges: Challenge[] | null,
                                            locations: CapitanLocation[] | null): string[] {
  function getEventTypeForId(eventTypeId: number): EventType | null {
    for (const eventType of eventTypes) {
      if (eventType.id === eventTypeId) { return eventType; }
    }
    return null;
  }

  function getProficiencyForId(proficiencyId: number): Proficiency | null {
    for (const proficiency of proficiencies) {
      if (proficiency.id === proficiencyId) { return proficiency; }
    }
    return null;
  }

  function getAssociationForId(assocId: number): Association | null {
    for (const assoc of associations) {
      if (assoc.id === assocId) { return assoc; }
    }
    return null;
  }

  function getChallengeForId(challengeId: number): Challenge | null {
    for (const challenge of challenges) {
      if (challenge.id === challengeId) { return challenge; }
    }
    return null;
  }

  const activeFilters: string[] = [];

  if (propExists(cfs.age_min) && propExists(cfs.age_max)) {
    if (cfs.age_min === cfs.age_max) {
      activeFilters.push('Age is exactly ' + cfs.age_min.toString());
    } else {
      activeFilters.push(
        'Age is between ' + cfs.age_min.toString() +
        ' and ' + cfs.age_max.toString()
      );
    }
  } else if (propExists(cfs.age_min)) {
    activeFilters.push('Age is at least ' + cfs.age_min.toString());
  } else if (cfs.age_max !== undefined) {
    activeFilters.push('Age is at most ' + cfs.age_max.toString());
  }

  if (propExists(cfs.has_active_membership)) {
    if (cfs.has_active_membership) {
      if (cfs.member_active_membership_name) {
        activeFilters.push(
          'Has an active membership whose name contains "' + cfs.member_active_membership_name + '"'
        );
      } else {
        activeFilters.push('Has an active membership');
      }
    } else {
      activeFilters.push('Does not have an active membership');
    }
  }

  if (propExists(cfs.has_active_youth_programming_membership)) {
    if (cfs.has_active_youth_programming_membership) {
      activeFilters.push('Has an active youth programming membership');
    } else {
      activeFilters.push('Does not have an active youth programming membership');
    }
  }

  if (propExists(cfs.has_active_non_youth_programming_membership)) {
    if (cfs.has_active_non_youth_programming_membership) {
      activeFilters.push('Has an active non-youth programming membership');
    } else {
      activeFilters.push('Does not have an active non-youth programming membership');
    }
  }

  if (propExists(cfs.has_membership_with_future_start)) {
    if (cfs.has_membership_with_future_start) {
      activeFilters.push('Has a membership which is set to start in the future');
    } else {
      activeFilters.push('Does not have a membership which is set to start in the future');
    }
  }

  if (propExists(cfs.has_frozen_membership)) {
    if (cfs.has_frozen_membership) {
      activeFilters.push('Has a frozen membership');
    } else {
      activeFilters.push('Does not have a frozen membership');
    }
  }

  if (
    propExists(cfs.first_membership_timestamp_min) ||
    propExists(cfs.first_membership_timestamp_max) ||
    propExistsAndIsNonEmpty(cfs.first_membership_timestamp_range)
  ) {
    activeFilters.push(
      getActiveDateRangeFilterText(
        'First became a member',
        cfs.first_membership_timestamp_min,
        cfs.first_membership_timestamp_max,
        cfs.first_membership_timestamp_range
      )
    );
  }

  if (propExists(cfs.number_of_available_entry_passes_min) && propExists(cfs.number_of_available_entry_passes_max)) {
    if (cfs.number_of_available_entry_passes_min === cfs.number_of_available_entry_passes_max) {
      activeFilters.push(
        'Has exactly ' + cfs.number_of_available_entry_passes_min.toString() + ' entry passes'
      );
    } else {
      activeFilters.push(
        'Has between ' + cfs.number_of_available_entry_passes_min.toString() +
        ' and ' + cfs.number_of_available_entry_passes_max.toString() + ' entry passes'
      );
    }
  } else if (propExists(cfs.number_of_available_entry_passes_min)) {
    activeFilters.push(
      'Has at least ' + cfs.number_of_available_entry_passes_min.toString() + ' entry passes'
    );
  } else if (propExists(cfs.number_of_available_entry_passes_max)) {
    activeFilters.push(
      'Has at most ' + cfs.number_of_available_entry_passes_max.toString() + ' entry passes'
    );
  }

  if (
    propExists(cfs.created_at_min) ||
    propExists(cfs.created_at_max) ||
    propExistsAndIsNonEmpty(cfs.created_at_range)
  ) {
    activeFilters.push(
      getActiveDateRangeFilterText(
        'Account created',
        cfs.created_at_min,
        cfs.created_at_max,
        cfs.created_at_range
      )
    );
  }

  if (propExists(cfs.address_postcode)) {
    if (cfs.address_postcode.split(',').length > 1) {
      activeFilters.push(
        orgConfigService.getPostcodeName(false, false) + ' is one of: ' + cfs.address_postcode
      );
    } else {
      activeFilters.push(
        orgConfigService.getPostcodeName(false, false) + ' is ' + cfs.address_postcode
      );
    }
  }

  if (propExists(cfs.gender)) {
    switch (cfs.gender) {
      case Gender.male:
        activeFilters.push('Gender is male');
        break;
      case Gender.female:
        activeFilters.push('Gender is female');
        break;
      case Gender.nonbinary:
        activeFilters.push('Gender is nonbinary');
        break;
      case Gender.prefer_not_to_say:
        activeFilters.push('Gender is \'prefer not to say\'');
        break;
    }
  }

  if (propExists(cfs.has_opted_in_to_marketing)) {
    if (cfs.has_opted_in_to_marketing) {
      activeFilters.push('Has opted in to marketing');
    } else {
      activeFilters.push('Has not opted in to marketing');
    }
  }

  if (propExists(cfs.check_in_count_min) && propExists(cfs.check_in_count_max)) {
    if (cfs.check_in_count_min === cfs.check_in_count_max) {
      activeFilters.push(
        'Has exactly ' + cfs.check_in_count_min.toString() + ' lifetime check-ins'
      );
    } else {
      activeFilters.push(
        'Has between ' + cfs.check_in_count_min.toString() +
        ' and ' + cfs.check_in_count_max.toString() + ' check-ins'
      );
    }
  } else if (propExists(cfs.check_in_count_min)) {
    activeFilters.push(
      'Has at least ' + cfs.check_in_count_min.toString() + ' lifetime check-ins'
    );
  } else if (propExists(cfs.check_in_count_max)) {
    activeFilters.push(
      'Has at most ' + cfs.check_in_count_max.toString() + ' lifetime check-ins'
    );
  }

  if (propExists(cfs.tags)) {
    activeFilters.push(...getMatchModeFilterTagsForNames(
      cfs.tags.split(','),
      cfs.tags_match,
      'Has any of these tags: ',
      tag => 'Has "' + tag + '" tag',
    ));
  }

  if (propExistsAndIsNonEmpty(cfs.has_event_type_reservation)) {
    if (eventTypes === null) {
      activeFilters.push('Loading...');
    } else {
      const filterParts = cfs.has_event_type_reservation.split(':::');

      let filterText = 'Has an event reservation';

      const eventTypeId = parseInt(filterParts[0], 10);
      if (!isNaN(eventTypeId)) {
        filterText += ' of type ';

        const eventType = getEventTypeForId(eventTypeId);

        if (eventType) { filterText += eventType.name; }
        else { filterText += '<unknown>'; }
      }

      if (propExistsAndIsNonEmpty(filterParts[1]) || propExistsAndIsNonEmpty(filterParts[2])) {
        filterText = getFilterTextForDateRange(
          filterText,
          filterParts[1],
          filterParts[2]
        );
      }

      activeFilters.push(filterText);
    }
  }

  if (
    propExists(cfs.latest_check_in_timestamp_min) ||
    propExists(cfs.latest_check_in_timestamp_max) ||
    propExistsAndIsNonEmpty(cfs.latest_check_in_timestamp_range) ||
    propExists(cfs.latest_check_in_timestamp_days_min) ||
    propExists(cfs.latest_check_in_timestamp_days_max)
  ) {
    activeFilters.push(
      getActiveDateRangeFilterText(
        'Latest check-in',
        cfs.latest_check_in_timestamp_min,
        cfs.latest_check_in_timestamp_max,
        cfs.latest_check_in_timestamp_range,
        cfs.latest_check_in_timestamp_days_min,
        cfs.latest_check_in_timestamp_days_max
      )
    );
  }

  if (propExistsAndIsNonEmpty(cfs.had_check_in_during_date_range)) {
    const filterParts = cfs.had_check_in_during_date_range.split(':::');

    let filterText = 'Checked in at least once';

    if (propExistsAndIsNonEmpty(filterParts[0]) || propExistsAndIsNonEmpty(filterParts[1])) {
      filterText = getFilterTextForDateRange(
        filterText,
        filterParts[0],
        filterParts[1]
      );
    }
    if (propExistsAndIsNonEmpty(filterParts[2])) {
      filterText += (
        ' with entry method "' +
        getCheckInEntryMethodDisplay(filterParts[2].trim() as CheckInEntryMethod) +
        '"'
      );
    }

    activeFilters.push(filterText);
  }

  if (propExistsAndIsNonEmpty(cfs.has_proficiencies)) {
    if (proficiencies === null) {
      activeFilters.push('Loading...');
    } else {
      activeFilters.push(...getMatchModeFilterTags(
        cfs.has_proficiencies,
        cfs.has_proficiencies_match,
        proficiencyId => {
          const proficiency = getProficiencyForId(proficiencyId);
          return proficiency ? proficiency.name : null;
        },
        'Has any of these proficiencies: ',
        name => 'Has the ' + name + ' proficiency',
      ));
    }
  }

  if (propExistsAndIsNonEmpty(cfs.has_association_memberships)) {
    if (associations === null) {
      activeFilters.push('Loading...');
    } else {
      activeFilters.push(...getMatchModeFilterTags(
        cfs.has_association_memberships,
        cfs.has_association_memberships_match,
        assocId => {
          const assoc = getAssociationForId(assocId);
          return assoc ? assoc.name : null;
        },
        'Has any of these associations: ',
        name => 'Has the ' + name + ' association',
      ));
    }
  }

  if (propExistsAndIsNonEmpty(cfs.has_challenge)) {
    if (challenges === null) {
      activeFilters.push('Loading...');
    } else {
      const filterParts = cfs.has_challenge.split(':::');

      let isComplete = null;
      if (propExistsAndIsNonEmpty(filterParts[1])) {
        const isCompleteInput = filterParts[1].toLowerCase();
        if (isCompleteInput === 'true') { isComplete = true; }
        else if (isCompleteInput === 'false') { isComplete = false; }
      }

      let allModePrefix = 'Has the ';
      let anyModePrefix = 'Has any of these challenges: ';
      if (isComplete === true) {
        allModePrefix = 'Completed the ';
        anyModePrefix = 'Completed any of these challenges: ';
      } else if (isComplete === false) {
        allModePrefix = 'Has not completed the ';
        // NOT "has not completed any of these": that reads as "completed none",
        // but the filter matches AT LEAST ONE incomplete challenge.
        anyModePrefix = 'Has not completed at least one of these challenges: ';
      }

      let challengeTags = getMatchModeFilterTags(
        filterParts[0],
        cfs.has_challenge_match,
        challengeId => {
          const challenge = getChallengeForId(challengeId);
          return challenge ? challenge.name : null;
        },
        anyModePrefix,
        name => allModePrefix + name + ' challenge',
      );

      if (propExistsAndIsNonEmpty(filterParts[2]) || propExistsAndIsNonEmpty(filterParts[3])) {
        challengeTags = challengeTags.map(challengeTag => getFilterTextForDateRange(
          challengeTag,
          filterParts[2],
          filterParts[3]
        ));
      }

      activeFilters.push(...challengeTags);
    }
  }

  if (propExistsAndIsNonEmpty(cfs.home_location_id)) {
    if (!locations) {
      activeFilters.push(
        'Loading...'
      );
    } else {
      let matchedLocation: CapitanLocation = null;
      for (const loc of locations) {
        if (loc.id === cfs.home_location_id) {
          matchedLocation = loc;
        }
      }
      if (matchedLocation !== null) {
        activeFilters.push(
          'Home Location: ' + matchedLocation.name
        );
      } else {
        activeFilters.push(
          'Home Location: [unknown location]'
        );
      }
    }
  }

  if (propExists(cfs.has_profile_image)) {
    if (cfs.has_profile_image) {
      activeFilters.push('Has a profile image');
    } else {
      activeFilters.push('Does not have a profile image');
    }
  }

  return activeFilters;
}

export function getScheduledEventsColumnHeaderName(column: ScheduledEventsColumn): string {
  switch (column.column_type) {
    case ScheduledEventsColumnType.reservationEmail:
      return 'Reservation Email';
    case ScheduledEventsColumnType.payment:
      return 'Payment';
    case ScheduledEventsColumnType.feeType:
      return 'Fee Type';
    case ScheduledEventsColumnType.addOns:
      return 'Add-ons';
    case ScheduledEventsColumnType.bookedBy:
      return 'Booked By';
    case ScheduledEventsColumnType.hasEmergencyContact:
      return 'Has Emergency Contact?';
    case ScheduledEventsColumnType.medicalNotes:
      return 'Medical Notes';
    case ScheduledEventsColumnType.capitanProfileField:
      return getCustomerFieldLabel(
        column.profile_field_name,
        null
      );
    case ScheduledEventsColumnType.customCustomerField:
      return column.custom_field_name;
    case ScheduledEventsColumnType.attendeeQuestion:
      return column.question_name;
    default:
      return '[Unknown Column]';
  }
}

export function getMembershipBillingIntervalStart(endDate: moment.Moment,
                                                  interval: MembershipInterval): moment.Moment {
  switch (interval) {
    case MembershipInterval.daily:
      return endDate.clone().subtract(1, 'days');
    case MembershipInterval.weekly:
      return endDate.clone().subtract(1, 'weeks');
    case MembershipInterval.biweekly:
      return endDate.clone().subtract(2, 'weeks');
    case MembershipInterval.fourWeeks:
      return endDate.clone().subtract(4, 'weeks');
    case MembershipInterval.monthly:
      return endDate.clone().subtract(1, 'months');
    case MembershipInterval.quarterly:
      return endDate.clone().subtract(3, 'months');
    case MembershipInterval.halfYearly:
      return endDate.clone().subtract(6, 'months');
    case MembershipInterval.yearly:
      return endDate.clone().subtract(1, 'years');
    default:
      throw new Error('Unknown membership billing interval value')
  }
}

/**
 * The share of `periodAmount` covering the unused remainder of a billing
 * period, for a prorated credit on an immediate cancellation. `now` is clamped
 * into the period at both ends: a prepaid membership can be paid for before it
 * starts, and an unclamped remainder then exceeds the period itself and
 * offers more credit than was paid (PRO-1439). Result is in [0, periodAmount];
 * 0 for a zero- or negative-length period.
 */
export function getProratedRemainingAmount(periodAmount: number,
                                           periodStart: moment.Moment,
                                           periodEnd: moment.Moment,
                                           now: moment.Moment): number {
  const periodLength = periodEnd.diff(periodStart, 'seconds');

  if (periodLength <= 0) { return 0; }

  const remainingFrom = moment.max(moment.min(now, periodEnd), periodStart);
  const remaining = periodEnd.diff(remainingFrom, 'seconds');

  return periodAmount * (remaining / periodLength);
}

export function getAllowedLocationsForEventType(eventType: EventType,
                                                allLocations: CapitanLocation[]): CapitanLocation[] {
  if (eventType === null) { return null; }
  if (allLocations === null) { return null; }

  if (eventType.has_limited_location_availability) {
    return allLocations.filter(loc =>
      eventType.allowed_location_ids.includes(loc.id)
    )
  } else {
    return allLocations;
  }
}

const RECENTLY_VIEWED_REPORTS_STORAGE_KEY = 'recentlyViewedReports';
const RECENTLY_VIEWED_REPORTS_NUM_STORED = 4;

export function getRecentlyViewedReports(): SavedReportViewReportType[] {
  const storedValue = localStorage.getItem(
    RECENTLY_VIEWED_REPORTS_STORAGE_KEY
  );
  return storedValue ? JSON.parse(storedValue) : [];
}

export function addToRecentlyViewedReports(report: SavedReportViewReportType) {
  const recentList = getRecentlyViewedReports().filter(r => r !== report);
  recentList.unshift(report);
  localStorage.setItem(
    RECENTLY_VIEWED_REPORTS_STORAGE_KEY,
    JSON.stringify(recentList.slice(0, RECENTLY_VIEWED_REPORTS_NUM_STORED))
  );
}

export enum RevenueSource {
  all = 'all',
  capitan = 'capitan'
}

const REVENUE_SOURCE_PREFERENCE_KEY = 'revenueSourcePreference';

export function getStoredRevenueSource(): RevenueSource {
  const storedValue = localStorage.getItem(REVENUE_SOURCE_PREFERENCE_KEY);
  if (storedValue === RevenueSource.capitan) {
    return RevenueSource.capitan;
  }
  return RevenueSource.all;
}

export function saveRevenueSource(source: RevenueSource) {
  localStorage.setItem(REVENUE_SOURCE_PREFERENCE_KEY, source);
}

/**
 * How a revenue figure is calculated (PRO-1346). `netOfFees` deducts processor
 * fees, which only exist for Capitan-collected payments — valid for every
 * source the reports offer (`all`, `capitan`).
 */
export enum RevenueCalculation {
  net = 'net',
  gross = 'gross',
  netOfFees = 'net_of_fees'
}

const REVENUE_CALCULATION_PREFERENCE_KEY = 'revenueCalculationPreference';

export function getStoredRevenueCalculation(): RevenueCalculation {
  const storedValue = localStorage.getItem(REVENUE_CALCULATION_PREFERENCE_KEY);
  if (storedValue === RevenueCalculation.gross) {
    return RevenueCalculation.gross;
  }
  if (storedValue === RevenueCalculation.netOfFees) {
    return RevenueCalculation.netOfFees;
  }
  return RevenueCalculation.net;
}

export function saveRevenueCalculation(calculation: RevenueCalculation) {
  localStorage.setItem(REVENUE_CALCULATION_PREFERENCE_KEY, calculation);
}
